/**
 * Data Adapter
 * Converts between API data format and Mock data format
 * Allows seamless switching between API and Mock data sources
 */

/**
 * Application field ID to category mapping
 * Based on API documentation
 */
const FIELD_TO_CATEGORY = {
  1: 'power',           // 安防 -> power (fallback to closest)
  2: 'power',           // 空调 -> power
  3: 'sensors',         // 电表 -> sensors
  4: 'signal',          // 信号链
  5: 'mcu',             // MCU
  6: 'wireless',        // 无线连接
  7: 'interface'        // 接口芯片
}

const DEFAULT_CATEGORY = 'power'

/**
 * Adapt a single product from API format to Mock format
 * @param {Object} apiProduct - Product object from API
 * @returns {Object} Product object in Mock format
 */
export function adaptProductFromApi(apiProduct) {
  if (!apiProduct) return null

  // Map application_field_id to category
  const category = FIELD_TO_CATEGORY[apiProduct.application_field_id] || DEFAULT_CATEGORY

  // Build specs array from API fields
  const specs = buildSpecs(apiProduct)

  // Build pricing array from current_price
  const pricing = buildPricingTiers(apiProduct.price?.current_price || 0)

  // Calculate stock status
  const stock = calculateStockStatus(apiProduct.stock_qty)

  // Determine tag based on status
  const tag = apiProduct.status === 1 ? 'New' : ''

  // 直接获取价格
  const currentPrice = apiProduct.price?.current_price || 0
  const listPrice = apiProduct.price?.list_price || 0
  const priceCurrency = apiProduct.price?.currency || 'CNY'

  // 调试日志
  console.log(`[数据适配] 产品: ${apiProduct.product_name}`, {
    原始price对象: apiProduct.price,
    提取的currentPrice: currentPrice,
    pricing数组: pricing,
    stock对象: stock
  })

  return {
    id: apiProduct.id,
    brand: 'Water Core',
    model: apiProduct.model || '',
    name: apiProduct.product_name || '',
    category,
    image: apiProduct.cover_url || 'https://via.placeholder.com/400x300/005eb8/ffffff?text=Product',
    tag,
    specs,
    certifications: [],
    pricing,
    stock,
    description: apiProduct.features || '',
    datasheet: apiProduct.datasheet_url || '',
    datasheetName: apiProduct.datasheet_name || '产品手册',
    // 直接使用 API 返回的价格字段
    currentPrice,
    listPrice,
    priceCurrency,
    applications: [],
    // Additional API fields for reference
    _apiData: {
      moq: apiProduct.moq,
      spq: apiProduct.spq,
      stock_qty: apiProduct.stock_qty,
      supply_mode: apiProduct.supply_mode,
      presale_enabled: apiProduct.presale_enabled,
      lead_time_days_min: apiProduct.lead_time_days_min,
      lead_time_days_max: apiProduct.lead_time_days_max,
      price_current: currentPrice,
      price_list: listPrice,
      price_currency: priceCurrency
    }
  }
}

/**
 * Adapt multiple products from API format
 * @param {Array} apiProducts - Array of product objects from API
 * @returns {Array} Array of products in Mock format
 */
export function adaptProductsFromApi(apiProducts) {
  if (!Array.isArray(apiProducts)) return []
  return apiProducts.map(product => adaptProductFromApi(product))
}

/**
 * Build specs array from API product fields
 * @param {Object} apiProduct - Product object from API
 * @returns {Array} Array of spec objects
 */
function buildSpecs(apiProduct) {
  const specs = []

  if (apiProduct.package) {
    specs.push({ label: '封装', value: apiProduct.package })
  }

  if (apiProduct.process_nm) {
    specs.push({ label: '工艺', value: `${apiProduct.process_nm}nm` })
  }

  if (apiProduct.vin_min && apiProduct.vin_max) {
    specs.push({ label: '输入电压', value: `${apiProduct.vin_min}V ~ ${apiProduct.vin_max}V` })
  }

  if (apiProduct.iout_max) {
    specs.push({ label: '输出电流', value: `${apiProduct.iout_max}A` })
  }

  if (apiProduct.moq) {
    specs.push({ label: '最小起订', value: `${apiProduct.moq}` })
  }

  return specs
}

/**
 * Build pricing tiers from API single price
 * Creates a price ladder: 1x, 0.95x, 0.85x, 0.75x
 * @param {number} basePrice - Base unit price from API
 * @returns {Array} Array of pricing tier objects
 */
function buildPricingTiers(basePrice) {
  if (!basePrice || basePrice <= 0) {
    return [{ quantity: 1, price: 0 }]
  }

  return [
    { quantity: 1, price: Number((basePrice * 1.0).toFixed(2)) },
    { quantity: 10, price: Number((basePrice * 0.95).toFixed(2)) },
    { quantity: 50, price: Number((basePrice * 0.85).toFixed(2)) },
    { quantity: 100, price: Number((basePrice * 0.75).toFixed(2)) },
    { quantity: 500, price: Number((basePrice * 0.70).toFixed(2)) },
    { quantity: 1000, price: Number((basePrice * 0.65).toFixed(2)) }
  ]
}

/**
 * Calculate stock status based on quantity
 * @param {number} stockQty - Stock quantity
 * @returns {Object} Stock status object
 */
function calculateStockStatus(stockQty) {
  let status = 'in-stock'

  if (stockQty <= 0) {
    status = 'out-of-stock'
  } else if (stockQty < 100) {
    status = 'low-stock'
  }

  // Estimate delivery time based on stock
  let deliveryTime = '现货最快2小时发货'
  if (status === 'low-stock') {
    deliveryTime = '现货最快6小时发货'
  } else if (status === 'out-of-stock') {
    deliveryTime = '预计7-14天发货'
  }

  return {
    available: stockQty,
    status,
    deliveryTime,
    deliveryDays: status === 'out-of-stock' ? '7-14' : '1'
  }
}

/**
 * Adapt API order format to Match the frontend expectations
 * This function is for reference, actual order data might not need transformation
 * @param {Object} apiOrder - Order object from API
 * @returns {Object} Adapted order object
 */
export function adaptOrderFromApi(apiOrder) {
  if (!apiOrder) return null

  return {
    id: apiOrder.id,
    order_no: apiOrder.order_no,
    user_id: apiOrder.user_id,
    status: apiOrder.status,
    remark: apiOrder.remark || '',
    created_at: apiOrder.created_at,
    updated_at: apiOrder.updated_at,
    items: Array.isArray(apiOrder.items) ? apiOrder.items : []
  }
}

/**
 * Adapt multiple orders from API format
 * @param {Array} apiOrders - Array of order objects from API
 * @returns {Array} Array of adapted order objects
 */
export function adaptOrdersFromApi(apiOrders) {
  if (!Array.isArray(apiOrders)) return []
  return apiOrders.map(order => adaptOrderFromApi(order))
}

export default {
  adaptProductFromApi,
  adaptProductsFromApi,
  adaptOrderFromApi,
  adaptOrdersFromApi,
  buildSpecs,
  buildPricingTiers,
  calculateStockStatus,
  FIELD_TO_CATEGORY
}
